import { directoryApi } from '@my/directory-api';
import type { ImportDraft } from './types';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Generate a party code in the house style — 3 uppercase letters + 4 digits
 *  (e.g. "SMQ9540"). Uniqueness isn't guaranteed here; the BE's unique index
 *  is the authority, and {@link commitDraft} regenerates on the rare collision. */
export function generatePartyCode(): string {
  let s = '';
  for (let i = 0; i < 3; i++) s += LETTERS[Math.floor(Math.random() * 26)];
  for (let i = 0; i < 4; i++) s += Math.floor(Math.random() * 10);
  return s;
}

/** The BE returns a Conflict with this message text for a duplicate
 *  (org, code, type); the HTTP layer flattens it to Error.message. */
function isDuplicateError(err: unknown): boolean {
  return err instanceof Error && /already exists/i.test(err.message);
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : 'Unexpected error';
}

async function safeDeleteAddress(addressId: string): Promise<void> {
  try {
    await directoryApi.deleteAddress(addressId);
  } catch {
    // best-effort orphan cleanup — ignore failures
  }
}

/**
 * Commit one draft by reusing the existing create endpoints:
 * **address-first, then party** (the create-party contract takes existing
 * address ids and stamps PartyId back). Per-row, fully self-contained, so a
 * failure leaves the row editable + retryable.
 *
 * Behaviour:
 *  - **Idempotent** — an already-`created` draft returns untouched; a retried
 *    `failed` draft reuses its previously-created address (no duplicate).
 *  - **Blank code** → auto-generated `3-letter+4-digit`; on the rare collision
 *    (BE 409) it regenerates and retries.
 *  - **Provided code that already exists** → `skipped`, and the just-created
 *    address is rolled back so no orphan is left behind.
 *  - **Any other error** → `failed` (address kept for the retry).
 *
 * Returns the updated draft (commit-state fields populated).
 */
export async function commitDraft(organisationId: string, draft: ImportDraft): Promise<ImportDraft> {
  if (draft.status_ === 'created' && draft.createdPartyId) return draft;

  // 1. Address (reuse one from a prior attempt to avoid duplicates).
  let addressId = draft.createdPrimaryAddressId;
  if (!addressId) {
    try {
      addressId = await directoryApi.createAddress({
        organisationId,
        addressType: 'Location',
        name: draft.addrName.trim() || draft.name.trim() || draft.fullAddress.split(',')[0]!.trim(),
        code: null,
        fullAddress: draft.fullAddress.trim(),
        placeId: draft.placeId || '',
        city: draft.city.trim() || null,
        postcode: draft.postcode.trim() || null,
        countryIsoCode: draft.country.trim(),
        latitude: draft.latitude,
        longitude: draft.longitude,
        coordinatesSource: null, // geocoded by Google with a place id - Directory infers it
        status: 'Active',
        serviceTags: null,
        partyId: null,
        siteId: null,
      });
    } catch (err) {
      return { ...draft, status_: 'failed', error: `Address: ${errMsg(err)}` };
    }
  }

  // 2. Party — with code-gen + conflict handling.
  const provided = draft.code.trim();
  let lastErr: unknown;
  for (let attempt = 0; attempt < 4; attempt++) {
    const code = provided || generatePartyCode();
    try {
      const partyId = await directoryApi.createParty({
        organisationId,
        code,
        name: draft.name.trim(),
        type: draft.type,
        subtype: draft.subtype,
        status: draft.status,
        primaryAddressId: addressId,
        billingName: draft.billingName.trim() || draft.name.trim(),
        billingAddressId: addressId,
        billingFrequency: draft.billingFrequency,
        paymentMethod: draft.paymentMethod,
        taxNumber: draft.taxNumber.trim() || null,
        taxSchemeCountryCode: draft.country.trim(),
        currencyIsoCode: draft.currencyIsoCode,
        tags: draft.tags.trim() || null,
      });
      return {
        ...draft,
        status_: 'created',
        assignedCode: code,
        createdPartyId: partyId,
        createdPrimaryAddressId: addressId,
        createdBillingAddressId: addressId,
        error: undefined,
      };
    } catch (err) {
      lastErr = err;
      if (isDuplicateError(err)) {
        if (provided) {
          // Existing party with this code → skip; clean up the orphan address.
          await safeDeleteAddress(addressId);
          return {
            ...draft,
            status_: 'skipped',
            createdPrimaryAddressId: undefined,
            error: `Code "${provided}" already exists — skipped`,
          };
        }
        continue; // auto-gen collision — regenerate and retry
      }
      // Non-conflict failure — keep the address so a retry reuses it.
      return { ...draft, status_: 'failed', createdPrimaryAddressId: addressId, error: errMsg(err) };
    }
  }

  return {
    ...draft,
    status_: 'failed',
    createdPrimaryAddressId: addressId,
    error: errMsg(lastErr) || 'Could not generate a unique code after several tries',
  };
}

/**
 * Run async workers over `items` with bounded concurrency, preserving the
 * "one row fails → others continue" guarantee (workers must not throw —
 * `commitDraft` already swallows its own errors into the returned draft).
 */
export async function runPool<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  concurrency = 5,
): Promise<void> {
  let cursor = 0;
  const lane = async () => {
    while (cursor < items.length) {
      const i = cursor++;
      await worker(items[i]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, lane));
}
